import React from 'react';
import cn from 'classnames';
import { EditRubricCard } from 'apps/sigma2-ui/src/components/rubrics/EditRubricCard';
import { RubricPreviewCategoryContent } from 'apps/sigma2-ui/src/components/rubrics/RubricPreviewCategoryContent';

const FloatingActionButton: React.FC<{
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}> = ({ ariaLabel, onClick, children, className }) => {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        'h-12 w-12 rounded-full bg-white shadow-[0_4px_20px_rgba(8,8,87,0.12)]',
        'flex items-center justify-center text-[#080857]',
        'transition-all duration-150 hover:scale-[1.03] hover:shadow-[0_6px_24px_rgba(8,8,87,0.18)]',
        'focus:outline-none focus:ring-2 focus:ring-[#6C63FF] focus:ring-offset-2',
        className,
      )}
    >
      {children}
    </button>
  );
};

const EditIcon: React.FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M4.16602 15.8337H5.35352L13.7285 7.45866L12.541 6.27116L4.16602 14.6462V15.8337ZM15.166 6.00033L13.9993 4.83366C13.8431 4.67743 13.6313 4.58966 13.4102 4.58966C13.1891 4.58966 12.9772 4.67743 12.821 4.83366L11.7077 5.94699L12.8952 7.13449L14.0085 6.02116C14.1648 5.86491 14.2525 5.65301 14.2525 5.43199C14.2525 5.21097 14.1648 4.99908 14.0085 4.84283L15.166 6.00033ZM3.33268 17.5003C3.11166 17.5003 2.89971 17.4125 2.74343 17.2562C2.58715 17.0999 2.49935 16.888 2.49935 16.667V14.3012C2.49935 14.0802 2.58715 13.8682 2.74343 13.712L12.2327 4.22283C12.7016 3.75399 13.3375 3.4906 14.0007 3.4906C14.6639 3.4906 15.2997 3.75399 15.7685 4.22283L16.9352 5.38949C17.404 5.85833 17.6674 6.49414 17.6674 7.15741C17.6674 7.82067 17.404 8.45649 16.9352 8.92533L7.44518 18.4145C7.28895 18.5708 7.07699 18.6587 6.85585 18.6587H3.33268V17.5003Z"
        fill="currentColor"
      />
    </svg>
  );
};

const DuplicateIcon: React.FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M6.66667 6.66699V4.83366C6.66667 4.39163 6.84226 3.96771 7.15482 3.65515C7.46738 3.34259 7.89131 3.16699 8.33333 3.16699H14.1667C14.6087 3.16699 15.0326 3.34259 15.3452 3.65515C15.6577 3.96771 15.8333 4.39163 15.8333 4.83366V10.667C15.8333 11.109 15.6577 11.5329 15.3452 11.8455C15.0326 12.1581 14.6087 12.3337 14.1667 12.3337H12.3333V14.167C12.3333 14.609 12.1577 15.0329 11.8452 15.3455C11.5326 15.6581 11.1087 15.8337 10.6667 15.8337H4.83333C4.39131 15.8337 3.96738 15.6581 3.65482 15.3455C3.34226 15.0329 3.16667 14.609 3.16667 14.167V8.33366C3.16667 7.89163 3.34226 7.46771 3.65482 7.15515C3.96738 6.84259 4.39131 6.66699 4.83333 6.66699H6.66667ZM8.33333 6.66699H10.6667C11.1087 6.66699 11.5326 6.84259 11.8452 7.15515C12.1577 7.46771 12.3333 7.89163 12.3333 8.33366V10.667H14.1667V4.83366H8.33333V6.66699ZM10.6667 8.33366H4.83333V14.167H10.6667V8.33366Z"
        fill="currentColor"
      />
    </svg>
  );
};

const DeleteIcon: React.FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M7.5 3.33366H12.5L13.3333 4.16699H16.6667V5.83366H3.33333V4.16699H6.66667L7.5 3.33366ZM5 7.50033H15V15.8337C15 16.2757 14.8244 16.6996 14.5118 17.0122C14.1993 17.3247 13.7754 17.5003 13.3333 17.5003H6.66667C6.22464 17.5003 5.80072 17.3247 5.48816 17.0122C5.17559 16.6996 5 16.2757 5 15.8337V7.50033ZM7.5 9.16699V15.8337H9.16667V9.16699H7.5ZM10.8333 9.16699V15.8337H12.5V9.16699H10.8333Z"
        fill="currentColor"
      />
    </svg>
  );
};

export interface EditRubricPreviewCategorySectionProps {
  label: string;
  tags: string[];
  criteria: {
    criteria: string;
    weight: number;
  }[];
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isReadOnlyMode: boolean;
}

export const EditRubricPreviewCategorySection: React.FC<
  EditRubricPreviewCategorySectionProps
> = (props) => {
  return (
    <div className="w-full flex flex-col items-center">
      <div className="relative group">
        <EditRubricCard>
          <RubricPreviewCategoryContent
            label={props.label}
            tags={props.tags}
            criteria={props.criteria}
          />
        </EditRubricCard>

        {/* Invisible hover bridge to keep buttons visible when moving mouse to them */}
        {!props.isReadOnlyMode && (
          <div className="absolute top-0 left-full h-full w-[88px] hidden md:block" />
        )}

        {!props.isReadOnlyMode && (
          <div
            className={cn(
              'absolute top-1/2 left-full ml-4 -translate-y-1/2',
              'hidden md:flex flex-col gap-4',
              'opacity-0',
              'transition-opacity duration-150',
              'group-hover:opacity-100',
              'group-focus-within:opacity-100',
            )}
          >
            <FloatingActionButton
              ariaLabel="Edit category"
              onClick={props.onEdit}
            >
              <EditIcon />
            </FloatingActionButton>
            <FloatingActionButton
              ariaLabel="Duplicate category"
              onClick={props.onDuplicate}
            >
              <DuplicateIcon />
            </FloatingActionButton>
            <FloatingActionButton
              ariaLabel="Delete category"
              onClick={props.onDelete}
            >
              <DeleteIcon />
            </FloatingActionButton>
          </div>
        )}
      </div>
    </div>
  );
};
